import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Badge, ToggleButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { FormFieldset } from 'src/shared/components/form/formFieldset';
import { StyledToggleButtonGroup } from 'src/shared/components/organisms/styledToggleButtonGroup';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';
import { AutogiroSearchFilter } from '../models/autogiroSearchFilter';
import { AutogiroDataGrid } from '../components/autogiroDataGrid';
import { AutogiroOverviewStatuses } from '../components/autogiroOverviewStatuses';
import { useAutogiroUnlinkedBadge } from '../hooks/useAutogiroUnlinkedBadge';

export const AutogiroMandateOverviewPage = () => {
  const isMounted = useIsMounted();
  const location = useLocation();
  const { t } = useTranslation(['invoice']);
  const [unlinkedCount] = useAutogiroUnlinkedBadge();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const defaultStatuses = useMemo(
    () => [
      AutogiroMandateStatusEnum.New,
      AutogiroMandateStatusEnum.PendingApproval,
      AutogiroMandateStatusEnum.PendingCancel,
      AutogiroMandateStatusEnum.Approved,
      AutogiroMandateStatusEnum.NotApproved,
      AutogiroMandateStatusEnum.Canceled,
      AutogiroMandateStatusEnum.Error,
    ],
    [],
  );
  const [autogiroSearchFilter, setAutogiroSearchFilter] = useLocalStorage(LocalStorageKeys.AutogiroConsentSearchFilter, {
    sortCol: 'autogiroMandate_id',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
    status: defaultStatuses,
  } as AutogiroSearchFilter);

  // When navigating from invoice dashboard with filterStatus state, apply it
  useEffect(() => {
    const state = location.state as { filterStatus?: AutogiroMandateStatusEnum[]; filterLinked?: string } | null;
    if (state?.filterStatus || state?.filterLinked) {
      setAutogiroSearchFilter((prev) => ({
        ...prev,
        ...(state.filterStatus && { status: state.filterStatus }),
        ...(state.filterLinked && { linkedFilter: state.filterLinked }),
        pageIndex: 0,
      }));
      // Clear the state so refreshing the page doesn't re-apply
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchFilter: AutogiroSearchFilter = useMemo(() => {
    return {
      filter: autogiroSearchFilter?.filter,
      pageSize: autogiroSearchFilter?.pageSize,
      pageIndex: autogiroSearchFilter?.pageIndex,
      sortCol: autogiroSearchFilter?.sortCol,
      sortDir: autogiroSearchFilter?.sortDir,
      status: autogiroSearchFilter?.status,
      linkedFilter: autogiroSearchFilter?.linkedFilter,
    } as AutogiroSearchFilter;
  }, [
    autogiroSearchFilter?.filter,
    autogiroSearchFilter?.pageIndex,
    autogiroSearchFilter?.pageSize,
    autogiroSearchFilter?.sortCol,
    autogiroSearchFilter?.sortDir,
    autogiroSearchFilter?.status,
    autogiroSearchFilter?.linkedFilter,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setAutogiroSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setAutogiroSearchFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setAutogiroSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setAutogiroSearchFilter, tempSearchString]);

  return (
    <>
      <PageFilterSection
        leftContent={
          <SearchField
            initialValue={searchFilter.filter || ''}
            onSearch={(newSearchString) => {
              setSearchInitialized(true);
              setTempSearchString(newSearchString);
            }}
          />
        }
        centerContent={
          <AutogiroOverviewStatuses
            initialValue={autogiroSearchFilter?.status || defaultStatuses}
            callback={(statuses) => {
              setAutogiroSearchFilter((prev) => ({
                ...prev,
                status: statuses,
                pageIndex: 0,
              }));
            }}
          />
        }
        rightContent={
          <FormFieldset legend={t('invoice:autogiro.labels.linked')} noPadding displayInline>
            <StyledToggleButtonGroup
              value={autogiroSearchFilter?.linkedFilter || 'all'}
              exclusive
              onChange={(_e, value) => {
                if (value !== null) {
                  setAutogiroSearchFilter((prev) => ({
                    ...prev,
                    linkedFilter: value === 'all' ? undefined : value,
                    pageIndex: 0,
                  }));
                }
              }}
              color='primary'
              size='small'
            >
              <ToggleButton value='all'>{t('common:button.all')}</ToggleButton>
              <ToggleButton value='linked'>{t('invoice:autogiro.labels.linkedOnly')}</ToggleButton>
              <ToggleButton value='unlinked' sx={{ pr: unlinkedCount > 0 ? 4 : undefined }}>
                <Badge
                  badgeContent={unlinkedCount}
                  color='error'
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16, top: 0, right: -18 } }}
                >
                  {t('invoice:autogiro.labels.unlinkedOnly')}
                </Badge>
              </ToggleButton>
            </StyledToggleButtonGroup>
          </FormFieldset>
        }
      />
      <AutogiroDataGrid
        loadingCallback={(x) => ConsoleHelper.log(x)}
        searchFilter={searchFilter}
        searchFilterCallback={(newFilter) =>
          setAutogiroSearchFilter((prev) => ({
            ...prev,
            ...newFilter,
          }))
        }
      />
    </>
  );
};
